import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { apiFetch, ApiError } from "../../api/client";
import {
  clockOffsetMs,
  finishVisibleAt,
  nextCheckInMs,
  type LessonFinishState,
} from "../../lib/board/lessonFinishClock";
import { mainSiteDashboardUrl } from "../../lib/mainSiteLinks";
import type { BoardSignalEvent } from "../../hooks/useFramePresentation";

type Outcome =
  | { kind: "completed"; status: string; message: string }
  | { kind: "not_completed" | "rejected" | "unavailable"; message: string };

/** Кто смотрит доску урока. */
export type LessonRole = "teacher" | "student";

type Props = {
  boardId: string;
  /**
   * teacher — хозяин доски, «Завершить занятие» с итогами;
   * student — пришёл по ссылке урока, «Завершить урок»;
   * null — не показываем ничего.
   */
  role: LessonRole | null;
  /** Ключ ссылки урока (`?t=`) — по нему доска узнаёт ученика. */
  shareToken?: string | null;
  /** Сообщить странице, урок ли это (на уроке прячется «Завершить сессию»). */
  onLesson?: (lesson: LessonFinishState | null) => void;
  /** Сигнал всем на доске; false — связи нет. */
  sendSignal: (kind: string, payload: Record<string, unknown>) => boolean;
  /** Сюда страница передаёт сигналы участников. */
  signalBus: { current: ((ev: BoardSignalEvent) => void) | null };
  /** Перед уходом в кабинет — дождаться, пока доска сохранится. */
  beforeLeave?: () => Promise<void>;
};

/** Сколько ученик ждёт ответа преподавателя (решение школы 30.10.2026). */
export const STUDENT_WAIT_MS = 20_000;

/** Сколько висит «Занятие проведено» перед уходом в кабинет. */
const LEAVE_DELAY_MS = 1_800;

const TOPIC_MAX = 500;
const REPORT_TEXT_MAX = 2000;

/** Сигналы урока на доске. */
const SIG_REQUEST = "lesson_end_request";
const SIG_REPLY = "lesson_end_reply";
const SIG_FINISHED = "lesson_finished";

const SCORES = [
  { value: 1, label: "Не включался" },
  { value: 2, label: "С трудом" },
  { value: 3, label: "Нормально" },
  { value: 4, label: "Хорошо" },
  { value: 5, label: "Отлично" },
] as const;

type ReportDraft = {
  topic: string;
  summary: string;
  homework: string;
  studentScore: number | null;
  parentComment: string;
};

const EMPTY_DRAFT: ReportDraft = {
  topic: "",
  summary: "",
  homework: "",
  studentScore: null,
  parentComment: "",
};

/**
 * Черновик держим до отправки: случайно закрыли окно или страницу —
 * набранное не пропало. Ключ — на доску, у каждого урока свои итоги.
 */
function draftKey(boardId: string): string {
  return `wb-lesson-report:${boardId}`;
}

/** Ключ, где раньше хранилась только тема, — подхватываем её. */
function legacyTopicKey(boardId: string): string {
  return `wb-lesson-topic:${boardId}`;
}

function readDraft(boardId: string): ReportDraft {
  try {
    const raw = window.sessionStorage.getItem(draftKey(boardId));
    if (raw) {
      const p = JSON.parse(raw) as Partial<ReportDraft>;
      return {
        topic: typeof p.topic === "string" ? p.topic : "",
        summary: typeof p.summary === "string" ? p.summary : "",
        homework: typeof p.homework === "string" ? p.homework : "",
        studentScore:
          typeof p.studentScore === "number" && p.studentScore >= 1 && p.studentScore <= 5
            ? p.studentScore
            : null,
        parentComment: typeof p.parentComment === "string" ? p.parentComment : "",
      };
    }
    const topic = window.sessionStorage.getItem(legacyTopicKey(boardId)) ?? "";
    return { ...EMPTY_DRAFT, topic };
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

function writeDraft(boardId: string, draft: ReportDraft | null): void {
  try {
    window.sessionStorage.removeItem(legacyTopicKey(boardId));
    if (draft) window.sessionStorage.setItem(draftKey(boardId), JSON.stringify(draft));
    else window.sessionStorage.removeItem(draftKey(boardId));
  } catch {
    /* приватный режим — черновик просто не переживёт закрытие */
  }
}

function newRequestId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Уйти в личный кабинет на сайте школы — портал сам откроет нужный по роли. */
async function leaveToCabinet(beforeLeave?: () => Promise<void>): Promise<void> {
  try {
    await beforeLeave?.();
  } catch {
    /* сохранение не дождались — уходим всё равно, правки уже в очереди */
  }
  window.location.href = mainSiteDashboardUrl();
}

/**
 * Завершение школьного урока — полоска под шапкой в последние минуты.
 *
 * Правила школы (30.10.2026):
 * — кнопка появляется у обоих за 5 минут до конца урока по расписанию;
 * — преподаватель заполняет итоги (всё необязательно), сайт школы проводит
 *   занятие, и преподаватель уходит в свой личный кабинет на сайте;
 * — ученик жмёт «Завершить урок»: если преподаватель уже завершил — сразу
 *   в кабинет; если ещё на уроке — у преподавателя спрашивается разрешение,
 *   а без ответа за 20 секунд ученика отпускаем.
 * Ключ сайта страница не знает: всё отправляет сервер доски.
 */
export function BoardLessonFinishStrip({
  boardId,
  role,
  shareToken,
  onLesson,
  sendSignal,
  signalBus,
  beforeLeave,
}: Props) {
  const [lesson, setLesson] = useState<LessonFinishState | null>(null);
  const offsetRef = useRef(0);
  const [due, setDue] = useState(false);
  // Колбэки страницы новые на каждый рендер — держим последние в ref,
  // чтобы подписка на сигналы и отсчёт ожидания не перезапускались.
  const onLessonRef = useRef(onLesson);
  onLessonRef.current = onLesson;
  const sendRef = useRef(sendSignal);
  sendRef.current = sendSignal;
  const beforeLeaveRef = useRef(beforeLeave);
  beforeLeaveRef.current = beforeLeave;
  // Уходим один раз, даже если ответ преподавателя и срок ожидания совпали.
  const leftRef = useRef(false);
  const leave = useCallback(() => {
    if (leftRef.current) return;
    leftRef.current = true;
    void leaveToCabinet(beforeLeaveRef.current);
  }, []);

  // --- преподаватель: итоги ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<ReportDraft>(() => readDraft(boardId));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneNote, setDoneNote] = useState<string | null>(null);
  /** Ученик просит отпустить: ждёт ответа преподавателя. */
  const [studentAsk, setStudentAsk] = useState<{ requestId: string; label: string } | null>(null);

  // --- ученик ---
  const [teacherFinished, setTeacherFinished] = useState(false);
  const [asking, setAsking] = useState<{ requestId: string; until: number } | null>(null);
  const [askLeft, setAskLeft] = useState(0);
  const [denied, setDenied] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const update = (patch: Partial<ReportDraft>) => {
    setDraft((d) => {
      const next = { ...d, ...patch };
      writeDraft(boardId, next);
      return next;
    });
  };

  const load = useCallback(async () => {
    if (!role) return;
    try {
      const receivedAt = Date.now();
      const q = role === "student" && shareToken ? `?t=${encodeURIComponent(shareToken)}` : "";
      const data = await apiFetch<{ lesson: LessonFinishState | null }>(
        `/api/v1/boards/${encodeURIComponent(boardId)}/lesson-finish${q}`
      );
      const next = data.lesson && (data.lesson.role ?? "teacher") === role ? data.lesson : null;
      if (next) offsetRef.current = clockOffsetMs(next.serverNow, receivedAt);
      setLesson(next);
      if (role === "student" && next?.finished) setTeacherFinished(true);
      onLessonRef.current?.(next);
    } catch {
      // Не урок школы или сервер занят — кнопки просто нет.
      setLesson(null);
      onLessonRef.current?.(null);
    }
  }, [boardId, role, shareToken]);

  useEffect(() => {
    if (!role) return;
    void load();
    // Расписание могут сдвинуть, пока доска открыта: сверяемся раз в 10 минут.
    const t = window.setInterval(() => void load(), 10 * 60_000);
    return () => window.clearInterval(t);
  }, [role, load]);

  // Точно к сроку проверяем, пора ли показать кнопку. Ученику кнопка нужна
  // и после того, как преподаватель завершил, — поэтому «завершено» здесь
  // для ученика не прячет её.
  useEffect(() => {
    if (!lesson) {
      setDue(false);
      return;
    }
    const view = role === "student" ? { ...lesson, finished: null } : lesson;
    let timer = 0;
    const tick = () => {
      setDue(finishVisibleAt(view, Date.now(), offsetRef.current));
      timer = window.setTimeout(tick, nextCheckInMs(lesson.showFrom, Date.now(), offsetRef.current));
    };
    tick();
    return () => window.clearTimeout(timer);
  }, [lesson, role]);

  // Сигналы участников: просьба ученика, ответ преподавателя, «урок завершён».
  const finishedRef = useRef(false);
  finishedRef.current = Boolean(lesson?.finished);
  const askingRef = useRef(asking);
  askingRef.current = asking;
  useEffect(() => {
    if (!role) return;
    signalBus.current = (ev: BoardSignalEvent) => {
      if (ev.isSelf) return;
      const p = ev.payload ?? {};
      if (role === "teacher" && ev.kind === SIG_REQUEST && typeof p.requestId === "string") {
        // Урок уже проведён — отпускаем без вопросов.
        if (finishedRef.current) {
          sendRef.current(SIG_REPLY, { requestId: p.requestId, allow: true });
          return;
        }
        setStudentAsk({ requestId: p.requestId, label: ev.displayLabel?.trim() || "Ученик" });
        return;
      }
      if (role === "student" && ev.kind === SIG_REPLY) {
        const cur = askingRef.current;
        if (!cur || p.requestId !== cur.requestId) return;
        setAsking(null);
        if (p.allow === true) {
          setLeaving(true);
          leave();
        } else {
          setDenied(true);
        }
        return;
      }
      if (role === "student" && ev.kind === SIG_FINISHED) {
        setTeacherFinished(true);
      }
    };
    return () => {
      signalBus.current = null;
    };
  }, [role, signalBus, leave]);

  // Ученик ждёт ответа не дольше 20 секунд — дальше отпускаем.
  useEffect(() => {
    if (!asking) return;
    let t = 0;
    const tick = () => {
      const left = asking.until - Date.now();
      setAskLeft(Math.max(0, Math.ceil(left / 1000)));
      if (left <= 0) {
        // Останавливаем сразу: до перерисовки интервал успел бы уйти ещё раз.
        window.clearInterval(t);
        setAsking(null);
        setLeaving(true);
        leave();
      }
    };
    t = window.setInterval(tick, 250);
    tick();
    return () => window.clearInterval(t);
  }, [asking, leave]);

  // У преподавателя вопрос ученика гаснет вместе со сроком ожидания.
  useEffect(() => {
    if (!studentAsk) return;
    const t = window.setTimeout(() => setStudentAsk(null), STUDENT_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [studentAsk]);

  // Esc закрывает окно итогов — но не посреди отправки: ответ сайта надо увидеть.
  useEffect(() => {
    if (!dialogOpen) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key !== "Escape" || sending) return;
      ev.stopPropagation();
      setDialogOpen(false);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [dialogOpen, sending]);

  const cleanTopic = draft.topic.replace(/\s+/g, " ").trim();

  async function finish(ev: FormEvent) {
    ev.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const data = await apiFetch<{ outcome: Outcome }>(
        `/api/v1/boards/${encodeURIComponent(boardId)}/lesson-finish`,
        {
          method: "POST",
          json: {
            // Пустую тему не шлём: сайт сам запишет «Занятие на онлайн-доске».
            topic: cleanTopic || undefined,
            summary: draft.summary,
            homework: draft.homework,
            studentScore: draft.studentScore ?? undefined,
            parentComment: draft.parentComment,
          },
        }
      );
      const outcome = data.outcome;
      if (outcome.kind === "completed") {
        writeDraft(boardId, null);
        setDialogOpen(false);
        setDoneNote(outcome.message || "Занятие проведено");
        setLesson((l) =>
          l ? { ...l, finished: { status: outcome.status, message: outcome.message } } : l
        );
        // Ученик узнаёт, что урок завершён, — его кнопка уводит сразу.
        sendSignal(SIG_FINISHED, {});
        window.setTimeout(leave, LEAVE_DELAY_MS);
      } else {
        // Окно остаётся открытым с итогами: поправили — нажали ещё раз.
        setError(outcome.message);
      }
    } catch (e) {
      setError(
        e instanceof ApiError && e.message
          ? e.message
          : "Не получилось связаться с сервером доски. Нажмите ещё раз."
      );
    } finally {
      setSending(false);
    }
  }

  function askTeacher() {
    if (leaving) return;
    setDenied(false);
    if (teacherFinished) {
      setLeaving(true);
      leave();
      return;
    }
    const requestId = newRequestId();
    // Связи нет — сигнал не ушёл: тогда просто ждём те же 20 секунд,
    // как если бы преподаватель не ответил.
    sendSignal(SIG_REQUEST, { requestId });
    setAsking({ requestId, until: Date.now() + STUDENT_WAIT_MS });
  }

  function answerStudent(allow: boolean) {
    if (!studentAsk) return;
    sendSignal(SIG_REPLY, { requestId: studentAsk.requestId, allow });
    setStudentAsk(null);
  }

  if (!role) return null;

  // --- ученик ---
  if (role === "student") {
    if (!lesson || !due) return null;
    return (
      <aside className="wb-lesson-finish" aria-label="Завершение урока" aria-live="polite">
        <span className="wb-lesson-finish__text">
          {leaving
            ? "Возвращаемся в личный кабинет…"
            : teacherFinished
              ? "Преподаватель завершил урок."
              : asking
                ? `Спрашиваем преподавателя… ${askLeft} с`
                : denied
                  ? "Преподаватель просит ещё немного задержаться."
                  : "Урок подходит к концу."}
        </span>
        {!asking && !leaving ? (
          <button type="button" className="wb-lesson-finish__btn" onClick={askTeacher}>
            {teacherFinished ? "В личный кабинет" : "Завершить урок"}
          </button>
        ) : null}
      </aside>
    );
  }

  // --- преподаватель ---
  const askPopup = studentAsk ? (
    <dialog className="wb-modal wb-modal--center wb-lesson-ask" open aria-label="Ученик хочет завершить урок">
      <div className="wb-modal__box">
        <h2 className="wb-modal__heading">{studentAsk.label} хочет завершить урок</h2>
        <p className="wb-modal__note">
          Если не ответить, через 20 секунд ученик вернётся в свой личный кабинет.
        </p>
        <div className="wb-modal__footer">
          <button
            type="button"
            className="wb-modal__btn wb-modal__btn--ghost"
            onClick={() => answerStudent(false)}
          >
            Пусть останется
          </button>
          <button
            type="button"
            className="wb-modal__btn wb-modal__btn--primary"
            onClick={() => answerStudent(true)}
          >
            Отпустить
          </button>
        </div>
      </div>
    </dialog>
  ) : null;

  if (doneNote) {
    return (
      <>
        <aside className="wb-lesson-finish wb-lesson-finish--done" role="status">
          <span className="wb-lesson-finish__text">✓ {doneNote} — возвращаемся в личный кабинет…</span>
        </aside>
        {askPopup}
      </>
    );
  }

  if (!lesson || !due) return askPopup;

  return (
    <>
      <aside className="wb-lesson-finish" aria-label="Завершение занятия">
        <span className="wb-lesson-finish__text">Урок подходит к концу.</span>
        <button
          type="button"
          className="wb-lesson-finish__btn"
          onClick={() => {
            setError(null);
            setDialogOpen(true);
          }}
        >
          Завершить занятие
        </button>
      </aside>
      {askPopup}

      {dialogOpen ? (
        <dialog className="wb-modal wb-modal--center wb-lesson-report" open aria-label="Итоги занятия">
          <div className="wb-modal__box wb-lesson-report__box">
            <h2 className="wb-modal__heading">Итоги занятия</h2>
            <form onSubmit={(e) => void finish(e)}>
              <label className="wb-label" htmlFor="lesson-report-topic">
                Тема занятия
              </label>
              <input
                id="lesson-report-topic"
                className="wb-input"
                value={draft.topic}
                onChange={(e) => update({ topic: e.target.value })}
                maxLength={TOPIC_MAX}
                placeholder="Например: Дроби — сложение и вычитание"
                autoFocus
                disabled={sending}
              />

              <label className="wb-label" htmlFor="lesson-report-summary">
                Что сделали на уроке
              </label>
              <textarea
                id="lesson-report-summary"
                className="wb-input wb-textarea"
                rows={3}
                value={draft.summary}
                onChange={(e) => update({ summary: e.target.value })}
                maxLength={REPORT_TEXT_MAX}
                placeholder="Разобрали правило, решили № 5–9, написали самостоятельную"
                disabled={sending}
              />

              <label className="wb-label" htmlFor="lesson-report-homework">
                Домашнее задание
              </label>
              <textarea
                id="lesson-report-homework"
                className="wb-input wb-textarea"
                rows={2}
                value={draft.homework}
                onChange={(e) => update({ homework: e.target.value })}
                maxLength={REPORT_TEXT_MAX}
                placeholder="Учебник, с. 42, № 12, 14"
                disabled={sending}
              />

              <span className="wb-label" id="lesson-report-score">
                Как работал ученик
              </span>
              <div
                className="wb-lesson-report__scores"
                role="radiogroup"
                aria-labelledby="lesson-report-score"
              >
                {SCORES.map((s) => {
                  const active = draft.studentScore === s.value;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`wb-lesson-report__score${active ? " is-active" : ""}`}
                      disabled={sending}
                      // Повторное нажатие снимает выбор — поле необязательное.
                      onClick={() => update({ studentScore: active ? null : s.value })}
                    >
                      <span className="wb-lesson-report__score-num">{s.value}</span>
                      <span className="wb-lesson-report__score-label">{s.label}</span>
                    </button>
                  );
                })}
              </div>

              <label className="wb-label" htmlFor="lesson-report-parent">
                Несколько слов родителю
              </label>
              <textarea
                id="lesson-report-parent"
                className="wb-input wb-textarea"
                rows={2}
                value={draft.parentComment}
                onChange={(e) => update({ parentComment: e.target.value })}
                maxLength={REPORT_TEXT_MAX}
                placeholder="Что получилось, над чем поработать дома"
                disabled={sending}
              />

              <p className="wb-modal__note">
                Все поля по желанию. Справку увидит родитель на сайте школы.
              </p>
              {error ? (
                <p className="wb-modal__note wb-lesson-finish__error" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="wb-modal__footer">
                <button
                  type="button"
                  className="wb-modal__btn wb-modal__btn--ghost"
                  onClick={() => setDialogOpen(false)}
                  disabled={sending}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="wb-modal__btn wb-modal__btn--primary"
                  disabled={sending}
                  aria-busy={sending}
                >
                  {sending ? "Проводим занятие…" : "Провести занятие"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      ) : null}
    </>
  );
}
